import { motion } from "framer-motion";
import { GlassCard, GradientText, ScreenShell, ShimmerButton } from "./shared";
import { LeadershipIllustration } from "./LeadershipIllustration";
import { FloatingShapes, MeetingSilhouettes } from "./decor";

export interface WelcomeData {
  participantName: string;
  role: string;
  company: string;
  competencies: string[];
  duration: string;
  decisions: number;
  situation: string;
  challenge: string;
  roleDescription: string;
  journey: string[];
}

export function WelcomeScreen({ data, onBegin }: { data: WelcomeData; onBegin?: () => void }) {
  return (
    <ScreenShell>
      <div className="mx-auto grid min-h-[640px] max-w-7xl grid-cols-1 px-4 py-10 md:grid-cols-[42%_58%] md:px-8">
        {/* LEFT */}
        <div className="relative flex flex-col justify-center gap-[22px] px-6 py-12 md:border-r md:px-10"
          style={{ borderColor:"rgba(255,255,255,0.05)" }}>
          <div className="pointer-events-none absolute inset-0 -z-10"
            style={{ background:"radial-gradient(circle at 30% 50%, rgba(139,92,246,0.25), transparent 60%)" }} />
          <FloatingShapes />
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-full"
              style={{ background:"linear-gradient(135deg, #8B5CF6, #e879f9)", boxShadow:"0 0 20px rgba(139,92,246,0.5)" }} />
            <div className="leading-tight">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/35">NeuCode Talent</div>
              <div className="text-[10px] tracking-[0.15em]" style={{ color:"#A78BFA" }}>
                Leadership Simulation{data.company ? ` · ${data.company}` : ""}
              </div>
            </div>
          </div>

          <div className="text-[14px] text-white/50">You are stepping into the role of</div>
          <h1 className="text-[44px] font-black leading-[1.1]" style={{ letterSpacing:"-0.02em" }}>
            <GradientText>{data.participantName}</GradientText>
          </h1>
          {data.role && <div className="text-[16px] text-white/55">{data.role}</div>}

          {data.competencies.length > 0 && (
            <div>
              <div className="mb-[10px] text-[10px] font-bold uppercase tracking-[0.2em] text-white/30">
                This simulation assesses
              </div>
              <div className="flex flex-wrap gap-2">
                {data.competencies.map((c) => (
                  <span key={c} className="rounded-full border px-3 py-1 text-[11px] font-medium"
                    style={{ background:"rgba(139,92,246,0.2)", color:"#A78BFA", borderColor:"rgba(139,92,246,0.4)" }}>
                    {c}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-4 text-[13px] text-white/50">
            <span>🕐 {data.duration}</span>
            <span>🎯 {data.decisions} decisions</span>
            <span>🔒 AI-scored, private</span>
          </div>

          <ShimmerButton className="w-full max-w-[280px]" onClick={onBegin}>
            Begin Simulation →
          </ShimmerButton>

          <div className="mt-4 hidden md:flex">
            <LeadershipIllustration />
          </div>
        </div>

        {/* RIGHT */}
        <div className="relative flex flex-col justify-center gap-3 px-6 py-12 md:px-9">
          <MeetingSilhouettes />
          <div className="relative text-[12px] font-bold uppercase tracking-[0.2em] text-white/30">Your Briefing</div>

          {data.situation && (
            <BriefCard color="#22D3EE" label="🏢 The Situation" body={data.situation} />
          )}
          {data.challenge && (
            <BriefCard color="#F59E0B" label="⚡ Your Challenge" body={data.challenge} />
          )}
          {data.roleDescription && (
            <BriefCard color="#A78BFA" label="🎯 Your Role" body={data.roleDescription} />
          )}

          {data.journey.length > 0 && (
            <>
              <div className="mt-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/30">
                Your Journey — {data.decisions} Decisions
              </div>
              <div className="grid grid-cols-5 gap-2">
                {data.journey.slice(0, 10).map((label, i) => (
                  <motion.div key={i}
                    initial={{ scale:0.5, opacity:0 }} animate={{ scale:1, opacity:1 }}
                    transition={{ type:"spring", stiffness:380, damping:14, delay:i*0.04 }}>
                    <JourneyNode label={label} active={i === 0} index={i + 1} />
                  </motion.div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </ScreenShell>
  );
}

function BriefCard({ color, label, body }: { color: string; label: string; body: string }) {
  return (
    <GlassCard className="p-5">
      <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color }}>{label}</div>
      <div className="text-[14px] leading-[1.65]" style={{ color:"#CBD5E1" }}>{body}</div>
    </GlassCard>
  );
}

function JourneyNode({ label, active, index }: { label:string; active:boolean; index:number }) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className={`flex h-9 w-9 items-center justify-center rounded-full text-[11px] font-bold ${active?"animate-pulse":""}`}
        style={{
          background: active ? "rgba(34,211,238,0.2)" : "rgba(255,255,255,0.04)",
          border: active ? "1px solid rgba(34,211,238,0.6)" : "1px solid rgba(255,255,255,0.08)",
          color: active ? "#22D3EE" : "rgba(255,255,255,0.3)",
          boxShadow: active ? "0 0 16px rgba(34,211,238,0.5)" : "none",
        }}>
        {active ? "▶" : index}
      </div>
      <div className="text-center text-[8px] uppercase tracking-wide"
        style={{ color: active?"#22D3EE":"rgba(255,255,255,0.3)" }}>
        {label}
      </div>
    </div>
  );
}
